import { createAsyncThunk } from '@reduxjs/toolkit';
import { authentication, app } from '@microsoft/teams-js';

import { me } from '../../apis/users';
import { forceLogout, getSBToken } from '../../apis/authentication';
import { setCookieForTeams } from '@common/helpers/cookies';
import { decodeJWT } from '@common/pages/config/helpers';
import { isTeamsApp } from './auth.helpers';

export const checkLogin = createAsyncThunk('auth/checkLogin', async (_, { rejectWithValue }) => {
  const redirectError = localStorage.getItem('redirectError');
  const isTeams = isTeamsApp();

  const excludedPaths = ['/login', '/index.html#/login', '/my/accounts', '/config', '/overview'];

  if (excludedPaths.includes(window.location.pathname)) {
    localStorage.removeItem('redirectError');
    return rejectWithValue('Excluded path or redirect error');
  }

  // A stale marker must not prevent the authoritative /me session check.
  localStorage.removeItem('redirectError');

  // The access-token cookie is set HttpOnly by the backend, so it is
  // invisible to document.cookie and its presence cannot be tested here.
  // A cached `user` is not a credential either. `me()` is the only
  // authority on whether this browser has a valid session.
  try {
    const userRes = await me();
    const user = userRes?.data;
    // A 200 is not enough: a misconfigured baseURL makes the request resolve
    // against the web origin, where the dev server/CDN answers the SPA
    // index.html with 200 text/html. Only a real user object counts.
    if (user && typeof user === 'object' && typeof user.id === 'string') {
      localStorage.removeItem('redirectError');
      localStorage.setItem('user', JSON.stringify(user));
      return user;
    }
    throw new Error('No user data');
  } catch (error) {
    if (!redirectError) {
      localStorage.setItem('redirectError', 'true');
    }

    let errorMessage = 'Authentication failed - redirecting to ';
    if (isTeams) {
      errorMessage += 'Teams config';
    } else {
      errorMessage += 'login';
    }

    return rejectWithValue(errorMessage);
  }
});

export const teamsLogin = createAsyncThunk(
  'auth/teamsLogin',
  async (shouldInitializeApp: boolean | undefined = true, { rejectWithValue }) => {
    try {
      if (shouldInitializeApp) {
        try {
          await app.initialize();
          await app.notifySuccess();
        } catch (error) {
          await app.notifyFailure({
            reason: app.FailedReason.Other,
            message: error instanceof Error ? error.message : 'App initialization failed',
          });
          throw error;
        }
      }

      const token = await authentication.getAuthToken({ silent: true });
      setCookieForTeams('ms-token', token);

      const response = await getSBToken(token, 'teams');

      if (!response || response.status === undefined) {
        throw new Error('Invalid response from login service');
      }

      switch (response.status) {
        case 200: {
          setCookieForTeams('x-access-token', response.data.access_token);

          const userInfo = response.data.id_token ? decodeJWT(response.data.id_token) : null;
          if (userInfo) {
            const userData: any = {
              id: userInfo.sub,
              name: userInfo.name,
              email: userInfo.email || userInfo.preferred_username,
              username: userInfo.preferred_username,
              given_name: userInfo.given_name,
              family_name: userInfo.family_name,
              picture: userInfo.picture,
              locale: userInfo.locale,
              tenant_id: userInfo.tid,
            };
            localStorage.setItem('user', JSON.stringify(userData));
            return { userData, shouldRedirect: true, redirectUrl: '/' };
          }
          throw new Error('User information could not be decoded from id_token');
        }
        case 203:
          return { shouldRedirect: true, redirectUrl: response.data.redirect_url };
        default:
          throw new Error(`Teams login failed with status: ${response.status}`);
      }
    } catch (_err) {
      return rejectWithValue({
        message: 'Teams login failed',
        shouldRedirect: true,
        redirectUrl: '/overview',
      });
    }
  },
);

export const logout = createAsyncThunk('auth/logout', async (_, { getState }) => {
  localStorage.removeItem('user');
  localStorage.removeItem('redirectError');

  await forceLogout();
});
